import type { FieldLike, FilterLeaf, SortEntry } from './types';
import { isFilterApplicable } from './operators';

/**
 * Клиентский эвалюатор фильтров и сортировки. Работает с произвольными Row,
 * извлекая значение поля через `getValue`. Семантика combinator'ов: SQL-precedence
 * (AND > OR), как в FilterLeafSchema (`packages/shared/src/schemas/views.ts`).
 *
 * Используется для DictionaryDetailPage и других мест, где данные фильтруются
 * на клиенте (сервер dictionaries-entries поддерживает только ?search=).
 */

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function asNum(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function asArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x));
  if (v === null || v === undefined) return [];
  return [String(v)];
}

function toLowerStr(v: unknown): string {
  if (Array.isArray(v)) return v.map((x) => String(x)).join(' ').toLowerCase();
  if (v === null || v === undefined) return '';
  return String(v).toLowerCase();
}

function evalLeaf(field: FieldLike | undefined, leaf: FilterLeaf, value: unknown): boolean {
  const op = leaf.operator;
  if (op === 'is_empty') return isEmpty(value);
  if (op === 'is_not_empty') return !isEmpty(value);

  const ft = field?.fieldType;
  const expected = leaf.value;

  if (ft === 'number') {
    const a = asNum(value);
    const b = asNum(expected);
    if (a === null || b === null) return false;
    switch (op) {
      case 'eq': return a === b;
      case 'neq': return a !== b;
      case 'gt': return a > b;
      case 'gte': return a >= b;
      case 'lt': return a < b;
      case 'lte': return a <= b;
      default: return false;
    }
  }

  if (ft === 'boolean') {
    const expectedBool =
      expected === true || expected === 'true' ? true : expected === false || expected === 'false' ? false : null;
    if (expectedBool === null) return false;
    return op === 'eq' ? value === expectedBool : value !== expectedBool;
  }

  if (ft === 'date') {
    const a = typeof value === 'string' ? Date.parse(value) : NaN;
    const b = typeof expected === 'string' ? Date.parse(expected) : NaN;
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    switch (op) {
      case 'eq': return a === b;
      case 'neq': return a !== b;
      case 'gt': return a > b;
      case 'gte': return a >= b;
      case 'lt': return a < b;
      case 'lte': return a <= b;
      default: return false;
    }
  }

  if (ft === 'multi_select' || ((ft === 'reference' || ft === 'user') && Array.isArray(value))) {
    const arr = asArray(value);
    const exp = asArray(expected);
    switch (op) {
      case 'contains_any': return exp.some((x) => arr.includes(x));
      case 'contains_all': return exp.every((x) => arr.includes(x));
      case 'not_contains': return !exp.some((x) => arr.includes(x));
      case 'in': return exp.includes(arr[0] ?? '');
      case 'not_in': return !exp.includes(arr[0] ?? '');
      case 'eq': return arr.length === 1 && arr[0] === String(expected);
      case 'neq': return !(arr.length === 1 && arr[0] === String(expected));
      default: return false;
    }
  }

  if (ft === 'select' || ft === 'reference' || ft === 'user') {
    const a = value === null || value === undefined ? '' : String(value);
    if (op === 'in' || op === 'not_in') {
      const exp = asArray(expected);
      return op === 'in' ? exp.includes(a) : !exp.includes(a);
    }
    const b = expected === null || expected === undefined ? '' : String(expected);
    return op === 'eq' ? a === b : op === 'neq' ? a !== b : false;
  }

  // text / textarea / markdown / url / fallback
  const a = toLowerStr(value);
  const b = typeof expected === 'string' ? expected.toLowerCase() : '';
  switch (op) {
    case 'eq': return a === b;
    case 'neq': return a !== b;
    case 'contains': return b !== '' && a.includes(b);
    case 'not_contains': return b !== '' && !a.includes(b);
    default: return false;
  }
}

export interface ApplyFiltersOptions<Row> {
  getValue: (row: Row, fieldSlug: string) => unknown;
  resolveField: (slug: string) => FieldLike | undefined;
}

/**
 * Применяет фильтры к строкам по семантике AND > OR (как в FilterLeaf.combinator).
 * Пустые/неполные leaf'ы (isFilterApplicable === false) игнорируются.
 */
export function applyFilters<Row>(
  rows: Row[],
  filters: FilterLeaf[],
  options: ApplyFiltersOptions<Row>,
): Row[] {
  const active = filters.filter(isFilterApplicable);
  if (active.length === 0) return rows;

  // Группируем по OR: каждая группа — массив AND-условий
  const groups: FilterLeaf[][] = [[]];
  for (let i = 0; i < active.length; i++) {
    const leaf = active[i]!;
    if (i === 0 || leaf.combinator !== 'or') {
      groups[groups.length - 1]!.push(leaf);
    } else {
      groups.push([leaf]);
    }
  }

  return rows.filter((row) =>
    groups.some((group) =>
      group.every((leaf) =>
        evalLeaf(options.resolveField(leaf.fieldSlug), leaf, options.getValue(row, leaf.fieldSlug)),
      ),
    ),
  );
}

export interface ApplySortOptions<Row> {
  getValue: (row: Row, fieldSlug: string) => unknown;
  resolveField: (slug: string) => FieldLike | undefined;
  /** Опциональный трансформер raw → label для reference (для строкового сравнения). */
  resolveLabel?: (fieldSlug: string, raw: unknown) => string;
}

export function applySorts<Row>(
  rows: Row[],
  sorts: SortEntry[],
  options: ApplySortOptions<Row>,
): Row[] {
  if (sorts.length === 0) return rows;
  const sorted = [...rows];
  sorted.sort((a, b) => {
    for (const s of sorts) {
      const fd = options.resolveField(s.fieldSlug);
      const av = options.getValue(a, s.fieldSlug);
      const bv = options.getValue(b, s.fieldSlug);
      const aEmpty = av === null || av === undefined || av === '';
      const bEmpty = bv === null || bv === undefined || bv === '';
      if (aEmpty && bEmpty) continue;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      let cmp = 0;
      if (fd?.fieldType === 'number') {
        const an = asNum(av) ?? 0;
        const bn = asNum(bv) ?? 0;
        cmp = an - bn;
      } else if (fd?.fieldType === 'boolean') {
        cmp = Number(av === true) - Number(bv === true);
      } else if ((fd?.fieldType === 'reference' || fd?.fieldType === 'user') && options.resolveLabel) {
        cmp = options.resolveLabel(s.fieldSlug, av).localeCompare(
          options.resolveLabel(s.fieldSlug, bv),
          'ru',
        );
      } else {
        const as = Array.isArray(av) ? (av as unknown[]).join(',') : String(av);
        const bs = Array.isArray(bv) ? (bv as unknown[]).join(',') : String(bv);
        cmp = as.localeCompare(bs, 'ru', { numeric: true });
      }
      if (cmp !== 0) return s.direction === 'asc' ? cmp : -cmp;
    }
    return 0;
  });
  return sorted;
}
